import React from "react";
import { motion } from "framer-motion";

const SURFACE_STYLE = {
  backgroundImage:
    "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.12) 100%)",
};

const ACTIVE_STYLE = {
  backgroundImage: "linear-gradient(180deg, #82A2FF 0%, #4A76F5 100%)",
};

export default function SegmentedModeToggle({
  value,
  options,
  onChange,
  className = "",
  style,
  knobClassName = "",
  knobWidth: knobWidthOverride,
  knobWidthByValue,
  knobLeftByValue,
  labelBoundsByValue,
  surfaceStyle,
  activeStyle,
  ariaLabel,
  buttonClassName = "",
  renderAccessory,
  measureKnob = false,
  knobInset = 4,
}) {
  const rootRef = React.useRef(null);
  const [measuredWidth, setMeasuredWidth] = React.useState(0);
  const safeOptions = Array.isArray(options) && options.length > 0 ? options : [];
  const activeIndex = Math.max(
    0,
    safeOptions.findIndex((option) => option.value === value)
  );
  React.useEffect(() => {
    if (!measureKnob) return undefined;
    const node = rootRef.current;
    if (!node) return undefined;

    const update = () => {
      const nextWidth = node.getBoundingClientRect().width;
      setMeasuredWidth(Number.isFinite(nextWidth) ? nextWidth : 0);
    };

    update();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(update);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [measureKnob]);

  const segmentPercent = safeOptions.length > 0 ? 100 / safeOptions.length : 100;
  const measuredSegmentWidth =
    measureKnob && measuredWidth > 0 && safeOptions.length > 0
      ? (measuredWidth - knobInset * 2) / safeOptions.length
      : null;
  const knobWidth =
    measuredSegmentWidth != null
      ? `${measuredSegmentWidth}px`
      : safeOptions.length > 0
      ? `calc((100% - 8px) / ${safeOptions.length})`
      : "calc(100% - 8px)";
  const knobLeft =
    measuredSegmentWidth != null
      ? `${knobInset + activeIndex * measuredSegmentWidth}px`
      : safeOptions.length > 0
      ? `calc(4px + ${activeIndex} * ((100% - 8px) / ${safeOptions.length}))`
      : "4px";
  const activeOption = safeOptions[activeIndex];
  const resolvedKnobWidth = knobWidthOverride || knobWidth;
  const resolvedKnobLeft =
    activeOption && knobLeftByValue?.[activeOption.value]
      ? knobLeftByValue[activeOption.value]
      : knobLeft;
  const activeKnobWidth =
    activeOption && knobWidthByValue?.[activeOption.value]
      ? knobWidthByValue[activeOption.value]
      : resolvedKnobWidth;

  return (
    <div
      ref={rootRef}
      className={`relative h-[40px] w-[199px] overflow-hidden rounded-[99px] border-[0.5px] border-white/30 shadow-[0_1px_0_rgba(0,0,0,0.04),0_4px_4px_rgba(0,0,0,0.04),0_10px_10px_rgba(0,0,0,0.06)] backdrop-blur-[10px] ${className}`}
      style={{ ...SURFACE_STYLE, ...(surfaceStyle || {}), ...(style || {}) }}
      role="tablist"
      aria-label={ariaLabel}
    >
      {safeOptions.length > 0 ? (
        <motion.div
          initial={false}
          className={`absolute top-1/2 h-[32px] -translate-y-1/2 rounded-[38px] border-[0.5px] border-white/20 ${knobClassName}`}
          style={{
            ...ACTIVE_STYLE,
            ...(activeStyle || {}),
          }}
          animate={{
            left: resolvedKnobLeft,
            width: activeKnobWidth,
          }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        />
      ) : null}

      {safeOptions.map((option, index) => {
        const active = option.value === value;
        const labelBounds = labelBoundsByValue?.[option.value];
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange?.(option.value)}
            className={`absolute top-0 z-10 flex h-full items-center justify-center text-center text-[16px] font-medium leading-none transition ${buttonClassName} ${
              active ? "text-white" : "text-white/50"
            }`}
            style={{
              left: labelBounds?.left || `${index * segmentPercent}%`,
              width: labelBounds?.width || `${segmentPercent}%`,
            }}
          >
            {option.label}
            {typeof renderAccessory === "function"
              ? renderAccessory(option, { active, index })
              : null}
          </button>
        );
      })}
    </div>
  );
}
